import type { TargetSource } from "@/features/stream-stats/api/stream-stats-queries"

export enum CardMode {
  Target = "target",
  Pb = "pb",
  Default = "default",
}

export enum StatusType {
  Reached = "reached",
  NewPb = "new-pb",
  NoData = "no-data",
  NoDataAvailable = "no-data-available",
  None = "none",
}

export type TargetCardStateInput = {
  currentValue: number | undefined
  targetValue: number | undefined
  isCurrentMonth: boolean
  targetSource: TargetSource
}

export type TargetCardState = {
  mode: CardMode
  isNoData: boolean
  isTargetReached: boolean
  statusType: StatusType
}

const isPositive = (value: number | undefined): value is number =>
  value !== undefined && Number.isFinite(value) && value > 0

const isDefinedNumber = (value: number | undefined): value is number =>
  value !== undefined && Number.isFinite(value)

export function deriveTargetCardState(
  input: TargetCardStateInput
): TargetCardState {
  const { currentValue, targetValue, isCurrentMonth, targetSource } = input

  let mode = CardMode.Pb
  if (targetSource === "CONTRACT") {
    mode = CardMode.Target
  } else if (targetSource === "DEFAULT") {
    mode = CardMode.Default
  }
  const hasCurrent = isDefinedNumber(currentValue)

  const isNoData = !hasCurrent && !isPositive(targetValue)

  const isTargetReached =
    mode === CardMode.Target &&
    currentValue !== undefined &&
    targetValue !== undefined &&
    currentValue >= targetValue

  const isNewPersonalBest =
    mode === CardMode.Pb &&
    !isCurrentMonth &&
    isPositive(targetValue) &&
    (currentValue ?? 0) > targetValue

  let statusType: StatusType
  if (isNoData) {
    statusType = StatusType.NoData
  } else if (isCurrentMonth || mode === CardMode.Default) {
    statusType = StatusType.None
  } else if (mode === CardMode.Target) {
    statusType = isTargetReached ? StatusType.Reached : StatusType.None
  } else {
    statusType = isNewPersonalBest ? StatusType.NewPb : StatusType.None
  }

  return {
    mode,
    isNoData,
    isTargetReached,
    statusType,
  }
}
